import { Period, PeriodTypeDay } from "@dto/period";
import { Item } from "@dto/records";
import { TW_DateCell, TW_DateCell_editing } from "@utils/twClasses";
import { displayDate, isoDateRegex } from "@utils/types";
import { useEffect, useState } from "react";
import { useModelStore, useUserStore } from "src/store";
import { AbstractCell } from "./AbstractCell";
import { CellProps } from "./GridCell";
import { useTranslation } from "react-i18next";
import { EditRightAccess, hasGlobalRightAccess, ObjectRightType, ViewRightType } from "@dto/Users";


export function PeriodDayCell({ pathKey, field, item, position, updateValue, isEditing, inputRef, blurTriggered }: CellProps) {
    const userRole = useModelStore(state => state.userRole);
    const user = useUserStore(state => state.user);
    const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
    const selectedView = useModelStore(state => state.selectedView);
    const hasEditAccessOnObject = hasGlobalRightAccess(user, userRole, ObjectRightType, EditRightAccess, selectedObjectInfos?.object?._key);
    const hasEditAccessOnView = hasGlobalRightAccess(user, userRole, ViewRightType, EditRightAccess, selectedView?._key);
    const hasEditAccess = hasEditAccessOnObject && hasEditAccessOnView;
    const readOnly = item.readOnly || !hasEditAccess ;

    const [text, setText] = useState<string | null>(null);
    const lang = useUserStore(state => state.lang);
    const wsColor = useModelStore(state => state.wsColor);
    const { t } = useTranslation();
    const [lastBlurTriggered, setLastBlurTriggered] = useState<number | null>(null);
    const format = selectedView?.formats?.get(pathKey ?? "") ?? field?.format;
    
    useEffect(() => {
        if (item?.value) {
            const period = Period.fromObject(item.value);
            setText(period?.isoDate() || "");
        } else {
            setText(null);
        }
    }, [item]);

    useEffect(() => {
        const newValue = textToValue(text);
        if (newValue && isEditing && lastBlurTriggered && blurTriggered && lastBlurTriggered !== blurTriggered && item?.value !== newValue) {
            updateValue(item, newValue);
        }
        setLastBlurTriggered(blurTriggered);
    }, [blurTriggered]);

    const updateLocalValue = (item: Item, value: any) => {
        const period = Period.fromObject(value);
        setText(period?.isoDate() || "");
        updateValue(item, value);
    }

    const textToValue = (text: string | null): any => {
        if (!text || text.length === 0) {
            return null;
        }
        try {
            if (isoDateRegex.test(text)) {
                return Period.create(text, PeriodTypeDay);
            }
        } catch (error) {
            console.error("period text is not valid: ", text, error);
        }
        return null;
    }

    const valueToText = (): string => {
        const period = Period.fromObject(item?.value);
        const dateStr = period ? period.isoDate() : "";
        return displayDate(dateStr, format, field.aggregation, t);
    }

    function isTextValid(value: string | null) {
        return true;
    }

    const renderReadOnlyContent = () => {
        return (
            <div className={TW_DateCell + (readOnly ? " cursor-not-allowed" : "")}>
                {valueToText()}
            </div>
        )
    }

    const renderEditingContent = (
        onTextChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
        onEnterKeyPress: (event: React.KeyboardEvent<HTMLInputElement>) => void,
        inputRef: React.RefObject<HTMLInputElement>,
    ) => {
        return (<>
            <input
                className={TW_DateCell_editing}
                type="date"
                lang={lang}
                value={text ?? ""}
                onKeyDown={onEnterKeyPress}
                onChange={onTextChange}
                autoFocus={true}
                style={{ borderColor: wsColor }}
                ref={inputRef}
            />
        </>)
    }

    const updateText = (text: string | null) => {
        setText(text);
        // updateValue(item, text);
    }

    return <AbstractCell
        item={item}
        position={position}
        updateValue={updateLocalValue}
        renderReadOnlyContent={renderReadOnlyContent}
        renderEditingContent={renderEditingContent}
        textToValue={textToValue}
        isTextValid={isTextValid}
        text={text}
        setText={updateText}
        isEditing={isEditing}
        inputRef={inputRef}
        readOnly={readOnly}
    />
}
